import { useTranslation } from 'react-i18next';
import { formatCurrency, formatDate, formatNumber, type SupportedLocale, type SupportedCurrency } from '@/lib/formatters';

/**
 * Hook that provides localization utilities
 * Combines i18n translation with formatting functions
 */
export function useLocale() {
  const { t, i18n } = useTranslation();
  
  const currentLocale = (i18n.language?.startsWith('sk') ? 'sk' : 'en') as SupportedLocale;
  const currentCurrency = (currentLocale === 'sk' ? 'EUR' : 'USD') as SupportedCurrency;

  return {
    // Translation function
    t,
    
    // Current locale info
    locale: currentLocale,
    currency: currentCurrency,
    isslovak: currentLocale === 'sk',
    
    // Language switching
    changeLanguage: (lng: string) => i18n.changeLanguage(lng),
    
    // Formatting functions with current locale
    formatCurrency: (amount: number, currency?: SupportedCurrency) => 
      formatCurrency(currentLocale, amount, currency || currentCurrency),
    
    formatDate: (date: Date | string) => 
      formatDate(currentLocale, date),
    
    formatNumber: (number: number, decimals?: number) => 
      formatNumber(currentLocale, number, decimals),
      
    // Region detection
    getRegion: () => currentLocale === 'sk' ? 'EU' : 'US',
    isUSUser: () => currentLocale === 'en'
  };
}

/**
 * Hook specifically for currency formatting based on region detection
 */
export function useCurrencyFormatter() {
  const { formatCurrency, currency, locale } = useLocale();
  
  return {
    formatCurrency,
    currency,
    locale,
    // Convenience method for invoice amounts
    formatInvoiceAmount: (amount: number) => formatCurrency(amount),
  };
}

/**
 * Hook for date formatting based on current locale
 */
export function useDateFormatter() {
  const { formatDate, locale } = useLocale();
  
  return {
    formatDate,
    locale,
    // Common date formatting patterns
    formatShortDate: (date: Date | string) => formatDate(date),
    formatInvoiceDate: (date: Date | string) => formatDate(date),
  };
}